// src/pages/Login.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/authContext';

export default function Login() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [email, setEmail] = useState(() => localStorage.getItem('da_last_email') || '');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(Boolean(localStorage.getItem('da_last_email')));
  const [showPassword, setShowPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [error, setError] = useState('');

  // Redirect if already logged in
  useEffect(() => {
    if (user) navigate('/');
  }, [user, navigate]);

  const emailValid = useMemo(
    () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()),
    [email]
  );

  const passwordValid = useMemo(() => password.length >= 6, [password]);
  const formValid = emailValid && passwordValid;

  const handleRememberToggle = () => {
    setRemember((prev) => !prev);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formValid) {
      setError('Enter a valid email and a password with at least 6 characters.');
      return;
    }

    setLoading(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (signInError) {
        setError(signInError.message || 'Login failed.');
        return;
      }

      if (remember) {
        localStorage.setItem('da_last_email', email.trim());
      } else {
        localStorage.removeItem('da_last_email');
      }

      navigate('/');
    } catch (err) {
      setError('Unexpected error during login.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError('');
    setOauthLoading(true);
    try {
      const { error: oauthErr } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin, // adjust if using a specific callback
        },
      });
      if (oauthErr) {
        setError(oauthErr.message || 'Google sign in failed.');
      }
      // Redirect happens automatically
    } catch {
      setError('Unexpected error with Google sign in.');
    } finally {
      setOauthLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid place-items-center px-4">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          {/* Header */}
          <div className="px-6 pt-6 pb-4">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Debate Academy" className="h-8 w-8" />
              <h1 className="text-2xl font-bold text-gray-900">Welcome back</h1>
            </div>
            <p className="mt-1 text-sm text-gray-600">
              Sign in to continue your training.
            </p>
          </div>

          {/* Body */}
          <div className="px-6 pb-6">
            {error && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="name@example.com"
                />
                {!emailValid && email.length > 0 && (
                  <p className="mt-1 text-xs text-red-600">Enter a valid email.</p>
                )}
              </div>

              {/* Password */}
              <div>
                <div className="flex items-center justify-between">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  {/* Link could go to a Reset Password page if you have one */}
                  {/* <Link to="/reset" className="text-xs text-blue-600 hover:underline">Forgot?</Link> */}
                </div>
                <div className="mt-1 relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-10 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute inset-y-0 right-0 px-3 text-gray-500 hover:text-gray-700"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
                {!passwordValid && password.length > 0 && (
                  <p className="mt-1 text-xs text-red-600">
                    Password should be at least 6 characters.
                  </p>
                )}
              </div>

              {/* Remember me */}
              <div className="flex items-center justify-between">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={handleRememberToggle}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Remember email
                </label>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading || !formValid}
                className="w-full rounded-lg bg-blue-600 px-4 py-2 text-white font-medium hover:bg-blue-500 disabled:opacity-50"
              >
                {loading ? 'Signing in…' : 'Sign in'}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-3 text-xs uppercase tracking-wider text-gray-500">
                  Or continue with
                </span>
              </div>
            </div>

            {/* OAuth */}
            <button
              type="button"
              onClick={handleGoogle}
              disabled={oauthLoading}
              className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-800 hover:bg-gray-50 disabled:opacity-50"
            >
              {oauthLoading ? 'Redirecting…' : 'Sign in with Google'}
            </button>

            {/* Footer */}
            <p className="mt-6 text-center text-sm text-gray-600">
              Do not have an account?{' '}
              <Link to="/signup" className="font-medium text-blue-600 hover:underline">
                Sign up
              </Link>
            </p>
          </div>
        </div>

        {/* Small brand footer */}
        <p className="mt-6 text-center text-xs text-gray-500">
          Debate Academy
        </p>
      </div>
    </div>
  );
}
